import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Menu,
  X,
  ShieldCheck,
  User,
  LogOut,
  ChevronDown,
  Inbox,
} from "lucide-react";
import { LOGO_URL, NAV_ITEMS } from "../lib/constants";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const nav = useNavigate();
  const menuRef = useRef(null);

  const isLanding = location.pathname === "/";
  const isAdmin = user?.role === "admin";
  const isCustomer = user?.role === "customer";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const onLogout = () => {
    logout();
    setMenuOpen(false);
    nav("/", { replace: true });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-8 h-16 lg:h-20 flex items-center justify-between">
        <a
          href="#inicio"
          data-testid="brand-logo-link"
          className="flex items-center gap-3"
        >
          <img
            src={LOGO_URL}
            alt="Pino Verde Salud Ocupacional"
            className="h-10 w-10 lg:h-12 lg:w-12 object-contain"
          />
          <div className="hidden sm:block leading-tight">
            <div className="font-display font-extrabold text-pv-green tracking-tight text-lg">
              PINO VERDE
            </div>
            <div className="overline text-pv-orange -mt-0.5">
              Salud Ocupacional
            </div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_ITEMS.map((item) =>
            isLanding ? (
              <a
                key={item.href}
                href={item.href}
                data-testid={`nav-link-${item.href.replace("#", "")}`}
                className="text-sm font-medium text-slate-700 hover:text-pv-green transition-colors"
              >
                {item.label}
              </a>
            ) : (
              <Link
                key={item.href}
                to={`/${item.href}`}
                data-testid={`nav-link-${item.href.replace("#", "")}`}
                className="text-sm font-medium text-slate-700 hover:text-pv-green transition-colors"
              >
                {item.label}
              </Link>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {!user ? (
            <Button
              asChild
              variant="outline"
              data-testid="header-login-link"
              className="rounded-sm border-pv-green text-pv-green hover:bg-pv-green hover:text-white font-semibold h-11 px-4"
            >
              <Link to="/login">
                <User size={16} className="mr-2" />
                Iniciar sesión
              </Link>
            </Button>
          ) : (
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                data-testid="user-menu-toggle"
                className="flex items-center gap-2 px-3 h-11 rounded-sm border border-pv-green text-pv-green hover:bg-pv-green hover:text-white font-semibold transition-colors"
              >
                {isAdmin ? (
                  <ShieldCheck size={16} />
                ) : (
                  <User size={16} />
                )}
                <span className="max-w-[160px] truncate">
                  {user.name?.split(" ")[0] || "Mi cuenta"}
                </span>
                <ChevronDown size={14} className={menuOpen ? "rotate-180 transition-transform" : "transition-transform"} />
              </button>
              {menuOpen && (
                <div
                  data-testid="user-menu"
                  className="absolute right-0 mt-2 w-60 bg-white border border-slate-200 rounded-sm shadow-xl shadow-emerald-900/5 overflow-hidden"
                >
                  <div className="px-4 py-3 border-b border-slate-100">
                    <div className="text-xs text-slate-400">
                      {isAdmin ? "Administrador" : "Cliente"}
                    </div>
                    <div className="text-sm font-semibold text-slate-800 truncate">
                      {user.name}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {user.email}
                    </div>
                  </div>
                  {isAdmin ? (
                    <Link
                      to="/admin"
                      onClick={() => setMenuOpen(false)}
                      data-testid="menu-go-admin"
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
                    >
                      <ShieldCheck size={14} className="text-pv-green" />
                      Panel admin
                    </Link>
                  ) : (
                    <>
                      <Link
                        to="/perfil"
                        onClick={() => setMenuOpen(false)}
                        data-testid="menu-go-profile"
                        className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
                      >
                        <User size={14} className="text-pv-green" />
                        Mi perfil
                      </Link>
                      <Link
                        to="/perfil"
                        onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
                      >
                        <Inbox size={14} className="text-pv-orange" />
                        Mis cotizaciones
                      </Link>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={onLogout}
                    data-testid="menu-logout"
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 border-t border-slate-100"
                  >
                    <LogOut size={14} />
                    Cerrar sesión
                  </button>
                </div>
              )}
            </div>
          )}
          <Button
            asChild
            data-testid="header-cta-quote"
            className="rounded-sm bg-pv-orange hover:bg-pv-orange/90 text-white font-semibold px-5 h-11"
          >
            {isLanding ? (
              <a href="#contacto">Solicitar Cotización</a>
            ) : (
              <Link to="/#contacto">Solicitar Cotización</Link>
            )}
          </Button>
        </div>

        <button
          type="button"
          aria-label="Abrir menú"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2 rounded-sm border border-slate-300"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-white border-t border-slate-200"
        >
          <div className="mx-auto max-w-7xl px-5 py-4 flex flex-col gap-3">
            {NAV_ITEMS.map((item) =>
              isLanding ? (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${item.href.replace("#", "")}`}
                  className="py-2 text-base font-medium text-slate-800 border-b border-slate-100"
                >
                  {item.label}
                </a>
              ) : (
                <Link
                  key={item.href}
                  to={`/${item.href}`}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${item.href.replace("#", "")}`}
                  className="py-2 text-base font-medium text-slate-800 border-b border-slate-100"
                >
                  {item.label}
                </Link>
              )
            )}
            {!user ? (
              <Button
                asChild
                variant="outline"
                data-testid="mobile-login-link"
                className="rounded-sm border-pv-green text-pv-green hover:bg-pv-green hover:text-white font-semibold mt-2"
              >
                <Link to="/login" onClick={() => setOpen(false)}>
                  <User size={16} className="mr-2" />
                  Iniciar sesión
                </Link>
              </Button>
            ) : (
              <>
                <Button
                  asChild
                  variant="outline"
                  className="rounded-sm border-pv-green text-pv-green hover:bg-pv-green hover:text-white font-semibold mt-2"
                  data-testid="mobile-profile-link"
                >
                  <Link
                    to={isAdmin ? "/admin" : "/perfil"}
                    onClick={() => setOpen(false)}
                  >
                    {isAdmin ? <ShieldCheck size={16} className="mr-2" /> : <User size={16} className="mr-2" />}
                    {isAdmin ? "Panel admin" : "Mi cuenta"}
                  </Link>
                </Button>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    onLogout();
                  }}
                  className="text-sm text-red-600 font-semibold py-2"
                  data-testid="mobile-logout"
                >
                  Cerrar sesión
                </button>
              </>
            )}
            <Button
              asChild
              data-testid="mobile-cta-quote"
              className="rounded-sm bg-pv-orange hover:bg-pv-orange/90 text-white font-semibold mt-2"
            >
              {isLanding ? (
                <a href="#contacto" onClick={() => setOpen(false)}>
                  Solicitar Cotización
                </a>
              ) : (
                <Link to="/#contacto" onClick={() => setOpen(false)}>
                  Solicitar Cotización
                </Link>
              )}
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
